/* @jsx h */
import { defineComponent, h } from "vue";
import { RouterLink } from "vue-router";
import { useLocalization } from "@lemmonhq/uikit";
import type { CatalogDashboard } from "@app/api/trpc/routers/catalog";

type Props = { counts: CatalogDashboard["counts"] };

const KNOWN_TYPES = ["food", "drink", "alcoholic_drink", "retail", "accommodation", "zero_rated"];

// What the catalog is made of: categories, extras, services, product types.
export const CatalogGlance = defineComponent<Props>({
  name: "CatalogGlance",
  props: ["counts"],
  setup(props) {
    const { t } = useLocalization();

    const stat = (to: string, label: string, value: number, of: number) => (
      <RouterLink to={to} class="rounded-lg bg-base-200 p-3 hover:bg-base-300/60">
        <dt class="text-xs text-base-content/50">{label}</dt>
        <dd class="text-lg font-semibold tabular-nums text-accent">
          {value}
          {of > value && <span class="text-xs font-normal text-base-content/40">{` / ${of}`}</span>}
        </dd>
      </RouterLink>
    );

    return () => {
      const c = props.counts;
      return (
        <div class="space-y-3">
          <dl class="grid grid-cols-3 gap-2">
            {stat("/app/catalog_editor/categories", t("catalog.nav.categories"), c.active_categories, c.categories)}
            {stat("/app/catalog_editor/extras", t("catalog.nav.extras"), c.available_extras, c.extras)}
            {stat("/app/catalog_editor/services", t("catalog.nav.services"), c.active_services, c.services)}
          </dl>
          {c.item_types.length > 0 && (
            <div class="flex flex-wrap gap-1.5">
              {c.item_types.map((it) => (
                <span key={it.item_type ?? "none"} class="badge badge-ghost gap-1 text-xs">
                  {KNOWN_TYPES.includes(it.item_type ?? "")
                    ? t(`catalog.overview.type_${it.item_type}`)
                    : t("catalog.overview.type_other")}
                  <span class="tabular-nums text-base-content/50">{it.count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
      );
    };
  },
});
